// =============================================================================
// First-party clickstream tracker
// =============================================================================
// One event stream, ours. Fires for anonymous AND logged-in visitors and
// stitches to a user once they identify. Feeds /api/analytics/collect/ via a
// batched navigator.sendBeacon so it never blocks a render or a navigation.
//
// What it captures:
//   * page_view on every route change — with document.referrer (where they came
//     from into the site) and cached UTM attribution.
//   * click on every actionable element — by its visible text and a short
//     selector. Never captures typed input values.
//   * custom milestones (signup_started, verification_completed, ...) via
//     trackEvent(name, props).
//
// Honors cookie consent: if the visitor has declined (or GPC is set), the
// tracker is inert — no ids minted, no events sent.

import { apiUrl } from "./api";
import { getConsent, mirrorToMeta } from "./analytics";
import { isNative } from "./platform";
import { redactPath } from "./tokenisedPaths";

const ANON_KEY = "sift_anon_id";
const SESSION_KEY = "sift_session";
const IDENTIFIED_KEY = "sift_identified_session";
const ATTRIBUTION_KEY = "sift_attribution"; // written by analytics.captureAttribution()

const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min sliding window
const FLUSH_INTERVAL_MS = 8000;
const MAX_QUEUE = 20; // flush early once the queue reaches this many events

let queue = [];
let flushTimer = null;
let listenersBound = false;
// Every A/B experiment this visitor is in, as { name: variant }. Stamped onto
// each event so the whole clickstream funnel is sliceable by arm.
//
// A map rather than a single value, because two experiments now run at once
// (get_started_flow and company_hero) and the previous single-slot version
// would have had the second assignment silently overwrite the first. That would
// not have failed loudly: events would simply have carried the wrong experiment
// name and the older test's numbers would have quietly become nonsense.
//
// Each arm is stamped under its own `exp_<name>` key. The legacy flat
// `experiment` / `variant` pair is still written for whichever experiment
// declares itself primary, so queries and dashboards written against the old
// shape keep working unchanged.
let experiments = {};          // { [name]: variant }
let primaryExperiment = null;  // name, for the legacy flat keys

// --- bot gate ---------------------------------------------------------------
// Crawlers/automation (Meta's ad crawler, Applebot, headless Chrome, ...) were
// polluting the funnel — e.g. a bot "clicking" a dead OAuth button logged as a
// signup_failed. Keep them out of the clickstream entirely. The server drops
// bot UAs too; this just avoids sending in the first place. No regex lookbehind
// (old webviews choke on it); 'cubot' is a phone brand, so exclude it by hand.
function isBot() {
  try {
    if (navigator.webdriver) return true;
    const ua = (navigator.userAgent || "").toLowerCase();
    if (!ua) return false;
    if (ua.includes("cubot")) return false;
    return /bot|crawl|spider|slurp|headless|externalhit|meta-externalads|whatsapp|telegram|discord|applebot|bingpreview|semrush|ahrefs|petalbot|bytespider|gptbot|chatgpt|inspectiontools|lighthouse|pagespeed|phantom|puppeteer|playwright|scrapy/.test(ua);
  } catch {
    return false;
  }
}

// --- consent gate -----------------------------------------------------------
// Native is hard-off. Apple rejected build 25 under 5.1.2(i), and the remedy we
// chose was to make the app genuinely not track rather than show an ATT prompt.
// The cookie-consent banner is web-only (App.jsx), so on native there is no way
// for a user to decline — leaving this tracker live would mint sift_anon_id and
// beacon page views/clicks from inside the app with no opt-out, contradicting
// what the privacy policy tells reviewers. Keep the app silent.
/* Anything rendering inside a frame is a preview, not a visit.
   The storefront editor shows the person their own public page in an iframe
   and remounts it on every save (keyed on savedAt), so each save wrote a
   page_view for /c/<slug> into the clickstream. On 14 Sep that made two
   new signups look like they were being bounced out of the editor and back
   to their profile five times in three minutes -- a bug that did not exist,
   read off events that were never visits.

   It is not only noise in a report: /c/ view counts, the funnel's landing
   rows and anything reading "pages per session" all absorbed it. A frame we
   render for somebody to look at is not somebody arriving. */
function inFrame() {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws, and that only happens when
    // we ARE framed.
    return true;
  }
}

function enabled() {
  try {
    if (isNative()) return false;
    if (inFrame()) return false;
    if (isBot()) return false;
    return getConsent() !== "declined";
  } catch {
    return true;
  }
}

// --- ids --------------------------------------------------------------------
function uuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  // Fallback for old webviews without crypto.randomUUID.
  return (
    "a" +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 12)
  );
}

export function getAnonymousId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "anon-nostorage";
  }
}

// Sliding session: same id while activity stays within the TTL, new id after a
// gap. Returns the (possibly refreshed) session id.
function getSessionId() {
  const now = Date.now();
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    let s = raw ? JSON.parse(raw) : null;
    if (!s || !s.id || now - (s.last || 0) > SESSION_TTL_MS) {
      s = { id: uuid(), last: now };
      // A brand-new session means a fresh identify handshake is due.
      try { sessionStorage.removeItem(IDENTIFIED_KEY); } catch { /* noop */ }
    } else {
      s.last = now;
    }
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    return s.id;
  } catch {
    return "session-nostorage";
  }
}

function attribution() {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    const a = JSON.parse(raw);
    return {
      utm_source: a.utm_source || "",
      utm_medium: a.utm_medium || "",
      utm_campaign: a.utm_campaign || "",
      utm_content: a.utm_content || "",
      utm_term: a.utm_term || "",
    };
  } catch {
    return {};
  }
}

// --- queue + flush ----------------------------------------------------------
function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

export function flush() {
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  const body = JSON.stringify({ anonymous_id: getAnonymousId(), events: batch });
  const url = apiUrl("/api/analytics/collect/");
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      // Send the raw string (content-type text/plain) — NOT a Blob typed
      // application/json. The API is a different origin, and application/json
      // is not CORS-safelisted, so that beacon needs a preflight it can't do
      // and gets dropped (especially on page unload). text/plain is safelisted
      // → no preflight → it delivers. The server parses the body as JSON.
      const ok = navigator.sendBeacon(url, body);
      if (ok) return;
    }
  } catch {
    /* fall through to fetch */
  }
  // Fallback: keepalive fetch, also text/plain to stay preflight-free.
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    /* give up silently */
  }
}

function push(evt) {
  if (!enabled()) return;
  // Stamp the current A/B arm onto every event so page_views, clicks, and
  // milestones can all be grouped by variant in the clickstream.
  const names = Object.keys(experiments);
  let metadata = evt.metadata;
  if (names.length) {
    metadata = { ...(evt.metadata || {}) };
    for (const name of names) metadata[`exp_${name}`] = experiments[name];
    if (primaryExperiment && experiments[primaryExperiment]) {
      metadata.experiment = primaryExperiment;
      metadata.variant = experiments[primaryExperiment];
    }
  }
  queue.push({
    ...evt,
    ...(metadata ? { metadata } : {}),
    anonymous_id: getAnonymousId(),
    session_id: getSessionId(),
    ts: new Date().toISOString(),
    ...attribution(),
  });
  if (queue.length >= MAX_QUEUE) flush();
  else scheduleFlush();
}

// Set (or clear) the A/B experiment the visitor is in. Called by
// lib/experiment.js right after assignment; subsequent events carry the tag.
export function setExperimentContext(experiment, variant) {
  if (!experiment || !variant) {
    experiments = {};
    primaryExperiment = null;
    return;
  }
  experiments[experiment] = variant;
  // Only the primary owns the legacy flat keys, so a second experiment cannot
  // rename the first one out from under queries already written against it.
  primaryExperiment = experiment;
}

// Add an experiment WITHOUT claiming the legacy flat keys. This is what a
// second, concurrent test uses: its arm rides along as `exp_<name>` and the
// original test keeps `experiment` / `variant` to itself.
export function registerExperiment(experiment, variant) {
  if (!experiment || !variant) return;
  experiments[experiment] = variant;
}

// --- public API -------------------------------------------------------------
export function trackPageView(path, title) {
  push({
    type: "page_view",
    path: redactPath(path || (typeof window !== "undefined" ? window.location.pathname : "")),
    title: (title || (typeof document !== "undefined" ? document.title : "")).slice(0, 300),
    // document.referrer stays the external entry referrer across SPA nav — it's
    // exactly "where they came from" into the site.
    referrer: typeof document !== "undefined" ? document.referrer : "",
  });
}

export function trackEvent(name, props = {}) {
  push({
    type: "custom",
    name: String(name).slice(0, 120),
    path: redactPath(typeof window !== "undefined" ? window.location.pathname : ""),
    metadata: props && typeof props === "object" ? props : {},
  });
  // Company-side steps go to Meta under the same name (lib/analytics.js).
  // Never let the pixel break our own record of the event.
  try { mirrorToMeta(String(name), props); } catch { /* pixel trouble is not ours to surface */ }
}

function shortSelector(el) {
  if (!el || !el.tagName) return "";
  let sel = el.tagName.toLowerCase();
  if (el.id) sel += `#${el.id}`;
  else if (el.getAttribute && el.getAttribute("data-testid")) sel += `[data-testid="${el.getAttribute("data-testid")}"]`;
  else if (el.className && typeof el.className === "string") {
    const cls = el.className.trim().split(/\s+/).slice(0, 2).join(".");
    if (cls) sel += `.${cls}`;
  }
  return sel.slice(0, 300);
}

function handleClick(e) {
  if (!enabled()) return;
  try {
    const el = e.target?.closest?.(
      "a, button, [role='button'], input[type='submit'], input[type='button'], [data-analytics]"
    );
    if (!el) return;
    // Prefer an explicit label, then visible text, then aria-label/value.
    const text =
      (el.getAttribute && el.getAttribute("data-analytics")) ||
      (el.innerText || el.textContent || "").trim() ||
      (el.getAttribute && el.getAttribute("aria-label")) ||
      el.value ||
      "";
    push({
      type: "click",
      path: redactPath(window.location.pathname),
      target_text: String(text).replace(/\s+/g, " ").trim().slice(0, 200),
      target_selector: shortSelector(el),
    });
  } catch {
    /* never let tracking break a click */
  }
}

// Link the current anonymous_id to the logged-in user, once per session.
export async function identify() {
  if (!enabled()) return;
  try {
    let already = false;
    try { already = sessionStorage.getItem(IDENTIFIED_KEY) === "1"; } catch { /* noop */ }
    if (already) return;
    getSessionId(); // ensure session exists / not expired
    const { fetchWithAuth } = await import("../utils/fetchWithAuth");
    await fetchWithAuth("/api/analytics/identify/", {
      method: "POST",
      json: { anonymous_id: getAnonymousId() },
    });
    try { sessionStorage.setItem(IDENTIFIED_KEY, "1"); } catch { /* noop */ }
  } catch {
    /* best-effort; a missed identify just leaves events anonymous */
  }
}

// Bind global listeners once. Called from App on mount.
export function initTracker() {
  if (isNative()) return; // see enabled(): no listeners bound in the app at all
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  document.addEventListener("click", handleClick, true);
  // Flush on the way out so the tail of a session isn't lost.
  const flushNow = () => flush();
  window.addEventListener("pagehide", flushNow);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
